import { useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  addReaction,
  removeReaction,
  type CommentContextMode,
  type CommentReactionGroup,
  type ReactionVocabItem,
} from '../../api/comments';
import styles from './ReactionBar.module.css';

// =============================================================================
// Shared reaction bar (DEV-726). Lifted from the chat MessageBubble reaction
// pattern, but driven by the per-mode vocabulary from the server (DB source of
// truth) rather than a hardcoded list. Project mode renders curated governance
// labels (Concur / Disagree / Noted); social mode renders the emoji glyphs.
// =============================================================================

interface ReactionBarProps {
  commentId: string;
  reactions: CommentReactionGroup[];
  vocab: ReactionVocabItem[];
  mode: CommentContextMode;
  disabled?: boolean;
  onChanged: () => void;
}

export function ReactionBar({ commentId, reactions, vocab, mode, disabled, onChanged }: ReactionBarProps) {
  const { t } = useAppTranslation('common');
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);

  const labelFor = (reactionType: string) => {
    if (mode === 'social') return reactionType; // glyph is its own label
    return vocab.find((v) => v.reactionType === reactionType)?.label ?? reactionType;
  };

  const toggle = async (reactionType: string, mine: boolean) => {
    if (busy || disabled) return;
    setBusy(true);
    try {
      if (mine) await removeReaction(commentId, reactionType);
      else await addReaction(commentId, reactionType);
      setPicking(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const active = reactions.filter((r) => r.count > 0);

  return (
    <div className={styles.bar}>
      {active.map((r) => (
        <button
          key={r.reactionType}
          type="button"
          disabled={disabled}
          className={`${styles.badge} ${r.mineReacted ? styles.mine : ''}`}
          onClick={() => toggle(r.reactionType, r.mineReacted)}
        >
          <span className={styles.glyph}>{labelFor(r.reactionType)}</span>
          <span className={styles.count}>{r.count}</span>
        </button>
      ))}

      {!disabled && vocab.length > 0 && (
        <div className={styles.pickWrap}>
          <button
            type="button"
            className={styles.reactBtn}
            aria-label={t('comments.react')}
            onClick={() => setPicking((v) => !v)}
          >
            <i className="ti ti-mood-smile" aria-hidden="true" />
            <span>{t('comments.react')}</span>
          </button>
          {picking && (
            <div className={styles.picker} role="menu">
              {vocab.map((v) => (
                <button
                  key={v.reactionType}
                  type="button"
                  className={styles.pickerBtn}
                  onClick={() => toggle(v.reactionType, false)}
                >
                  {mode === 'social' ? v.reactionType : v.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ReactionBar;
